import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { orderService } from '../services/api.service';
import { getProductImageUrl } from '../../services/api';
import {
  ArrowLeft,
  Package,
  Truck,
  CheckCircle2,
  Clock,
  User,
  Mail,
  Phone,
  Calendar,
  CreditCard,
  AlertCircle,
  XCircle,
  RefreshCw,
  ChevronRight,
  MapPin
} from 'lucide-react';

const OrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      const res = await orderService.getOrders();
      if (res && res.orders) {
        const found = res.orders.find(o => String(o.id) === String(id) || o.order_number === `#${id}` || o.order_number === id);
        setOrder(found || null);
      }
    } catch (err) {
      console.warn("Failed to load order details:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrderDetails();
  }, [id]);

  const [showShipModal, setShowShipModal] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [courierPartner, setCourierPartner] = useState('');

  const handleAdvanceStatus = async (nextStatus, extraData = {}) => {
    if (!order) return;
    try {
      setUpdating(true);
      const res = await orderService.updateStatus(order.id, nextStatus, extraData);
      if (res && res.order) {
        setOrder(res.order);
      }
      await fetchOrderDetails();
    } catch (err) {
      alert("Failed to advance order status.");
    } finally {
      setUpdating(false);
      setShowShipModal(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
        Loading order details...
      </div>
    );
  }

  if (!order) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h3 style={{ fontSize: '18px', fontWeight: 800, color: '#0f172a' }}>Order Not Found</h3>
        <button onClick={() => navigate('/admin/orders')} className="btn-secondary" style={{ marginTop: '16px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <ArrowLeft size={16} />
          <span>Back to Orders</span>
        </button>
      </div>
    );
  }

  const currentStatus = (order.order_status || 'Processing').toLowerCase();

  // Strict Sequential Progression Logic:
  // Processing -> Shipped -> Delivered
  // Cancelled is terminal
  let nextAllowedStatus = null;
  let nextActionLabel = '';
  let nextActionIcon = null;

  if (currentStatus === 'processing') {
    nextAllowedStatus = 'Shipped';
    nextActionLabel = 'Mark as Shipped';
    nextActionIcon = Truck;
  } else if (currentStatus === 'shipped') {
    nextAllowedStatus = 'Delivered';
    nextActionLabel = 'Mark as Delivered';
    nextActionIcon = CheckCircle2;
  }

  const canCancel = currentStatus === 'processing' || currentStatus === 'shipped';

  const renderStatusPill = (status) => {
    const s = status ? status.toLowerCase() : 'processing';
    let bg = '#fff7ed';
    let color = '#c2410c';
    let border = '#ffedd5';

    if (s === 'shipped') {
      bg = '#eff6ff';
      color = '#1d4ed8';
      border = '#bfdbfe';
    } else if (s === 'delivered') {
      bg = '#dcfce7';
      color = '#15803d';
      border = '#bbf7d0';
    } else if (s === 'cancelled') {
      bg = '#fef2f2';
      color = '#b91c1c';
      border = '#fecaca';
    }

    return (
      <span style={{ fontSize: '12px', fontWeight: 800, color, background: bg, border: `1px solid ${border}`, padding: '4px 12px', borderRadius: '6px', textTransform: 'capitalize' }}>
        {status || 'Processing'}
      </span>
    );
  };

  const NextIconComponent = nextActionIcon;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      
      {/* Top Header & Navigation */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            onClick={() => navigate('/admin/orders')}
            style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#ffffff', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#0f172a' }}
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <h2 style={{ fontSize: '22px', fontWeight: 900, color: '#0f172a', margin: 0 }}>
                Order {order.order_number || `#ORD-${order.id}`}
              </h2>
              {renderStatusPill(order.order_status)}
            </div>
            <p style={{ fontSize: '13px', color: '#64748b', margin: '4px 0 0 0' }}>
              Placed on {new Date(order.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}
            </p>
          </div>
        </div>

        <button onClick={fetchOrderDetails} className="btn-secondary" style={{ width: 'auto', padding: '0 16px', height: '40px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <RefreshCw size={15} />
          <span>Refresh Details</span>
        </button>
      </div>

      {/* STRICT SEQUENTIAL WORKFLOW ACTION BAR */}
      <div className="table-card-box" style={{ padding: '24px', background: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '20px' }}>
        <div>
          <div style={{ fontSize: '13px', fontWeight: 900, color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>
            Fulfillment Workflow Progression
          </div>
          <div style={{ fontSize: '13px', color: '#64748b' }}>
            {currentStatus === 'delivered' ? (
              <span style={{ color: '#16a34a', fontWeight: 700 }}>✔ Order is fully delivered. Workflow complete.</span>
            ) : currentStatus === 'cancelled' ? (
              <span style={{ color: '#dc2626', fontWeight: 700 }}>✖ Order has been cancelled. No further progression allowed.</span>
            ) : (
              <span>Follow the strict sequential lifecycle: <strong>Processing ➔ Shipped ➔ Delivered</strong>.</span>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {canCancel && (
            <button
              disabled={updating}
              onClick={() => {
                if (window.confirm("Are you sure you want to cancel this order?")) {
                  handleAdvanceStatus('Cancelled');
                }
              }}
              style={{ padding: '10px 18px', borderRadius: '10px', background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', fontWeight: 800, fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <XCircle size={16} />
              <span>Cancel Order</span>
            </button>
          )}

          {nextAllowedStatus && (
            <button
              disabled={updating}
              onClick={() => {
                if (nextAllowedStatus === 'Shipped') {
                  setShowShipModal(true);
                } else {
                  handleAdvanceStatus(nextAllowedStatus);
                }
              }}
              style={{ padding: '10px 20px', borderRadius: '10px', background: '#dc2626', border: 'none', color: '#ffffff', fontWeight: 900, fontSize: '13.5px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 4px 12px rgba(220, 38, 38, 0.2)' }}
            >
              {NextIconComponent && <NextIconComponent size={16} />}
              <span>{nextActionLabel}</span>
            </button>
          )}
        </div>
      </div>

      {/* MAIN CONTENT GRID */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: '24px' }}>
        
        {/* LEFT COLUMN: ITEMS LIST & SHIPPING INFO */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          
          {/* Purchased Products Table */}
          <div className="table-card-box" style={{ padding: '24px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 900, color: '#0f172a', margin: '0 0 16px 0', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Purchased Items ({Array.isArray(order.items) ? order.items.length : 1})
            </h3>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#64748b', textAlign: 'left' }}>
                    <th style={{ padding: '12px 16px', fontWeight: 800 }}>PRODUCT</th>
                    <th style={{ padding: '12px 16px', fontWeight: 800, textAlign: 'center' }}>PRICE</th>
                    <th style={{ padding: '12px 16px', fontWeight: 800, textAlign: 'center' }}>QTY</th>
                    <th style={{ padding: '12px 16px', fontWeight: 800, textAlign: 'right' }}>SUBTOTAL</th>
                  </tr>
                </thead>
                <tbody>
                  {(Array.isArray(order.items) ? order.items : []).map((it, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '14px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{ width: '48px', height: '48px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '4px', flexShrink: 0 }}>
                            <img src={getProductImageUrl(it.image_url || it.image || '/uploads/panel_72led.jpg')} alt="Panel" style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }} />
                          </div>
                          <div>
                            <div style={{ fontWeight: 800, color: '#0f172a' }}>{it.name || 'InfraEdge Panel'}</div>
                            {Array.isArray(it.selected_addons) && it.selected_addons.length > 0 && (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '3px' }}>
                                {it.selected_addons.map((addon, aIdx) => (
                                  <span key={aIdx} style={{ fontSize: '10px', fontWeight: 700, color: '#dc2626', background: '#fef2f2', border: '1px solid #fee2e2', padding: '1px 6px', borderRadius: '4px' }}>
                                    + {addon.addon_name || addon.name} (+£{Number(addon.addon_price || addon.price || 0).toFixed(2)})
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'center', fontWeight: 700, color: '#0f172a' }}>
                        £{Number(it.price || 0).toFixed(2)}
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'center', fontWeight: 800, color: '#0f172a' }}>
                        {it.quantity || 1}
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'right', fontWeight: 900, color: '#dc2626' }}>
                        £{(Number(it.price || 0) * Number(it.quantity || 1)).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Subtotal Summary Footer */}
            <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: '8px', width: '280px', marginLeft: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#64748b' }}>
                <span>Subtotal:</span>
                <span style={{ fontWeight: 800, color: '#0f172a' }}>£{Number(order.subtotal || order.total_amount || 0).toFixed(2)}</span>
              </div>
              {Array.isArray(order.items) && order.items.some(it => Array.isArray(it.selected_addons) && it.selected_addons.length > 0) && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', background: '#fef2f2', border: '1px solid #fee2e2', borderRadius: '8px', padding: '8px 10px', margin: '4px 0' }}>
                  <div style={{ fontSize: '10.5px', fontWeight: 800, color: '#dc2626', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Upgrades & Add-Ons</div>
                  {order.items.flatMap(it => it.selected_addons || []).map((addon, aIdx) => (
                    <div key={aIdx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11.5px', color: '#991b1b', fontWeight: 700 }}>
                      <span>+ {addon.addon_name || addon.name}</span>
                      <span>+£{Number(addon.addon_price || addon.price || 0).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              )}
              {Number(order.discount_amount) > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#dc2626' }}>
                  <span>Discount ({order.coupon_code || 'PROMO'}):</span>
                  <span>- £{Number(order.discount_amount).toFixed(2)}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#64748b' }}>
                <span>Shipping Fee:</span>
                <span style={{ fontWeight: 800, color: '#0f172a' }}>£{Number(order.shipping_fee || 0).toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '16px', fontWeight: 900, color: '#0f172a', paddingTop: '8px', borderTop: '1px solid #e2e8f0' }}>
                <span>Grand Total:</span>
                <span style={{ color: '#dc2626' }}>£{Number(order.total_amount || 0).toFixed(2)}</span>
              </div>
            </div>

          </div>

          {/* Delivery & Customer Information Card */}
          <div className="table-card-box" style={{ padding: '24px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 900, color: '#0f172a', margin: '0 0 16px 0', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Truck size={18} color="#dc2626" />
              <span>Delivery & Customer Details</span>
            </h3>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', fontSize: '13.5px' }}>
              <div>
                <div style={{ fontSize: '12px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', marginBottom: '6px' }}>Recipient Name</div>
                <div style={{ fontWeight: 800, color: '#0f172a' }}>
                  {order.shipping_address?.full_name || order.shipping_address?.name || order.user_name || 'Valued Customer'}
                </div>
              </div>

              <div>
                <div style={{ fontSize: '12px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', marginBottom: '6px' }}>Customer Email</div>
                <a href={`mailto:${order.user_email || order.guest_email}`} style={{ fontWeight: 700, color: '#dc2626', textDecoration: 'none' }}>
                  {order.user_email || order.guest_email || 'Guest User'}
                </a>
              </div>

              <div style={{ gridColumn: '1 / -1' }}>
                <div style={{ fontSize: '12px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', marginBottom: '6px' }}>Shipping Address (Delivery)</div>
                <div style={{ color: '#334155', lineHeight: 1.5, background: '#f8fafc', padding: '12px 16px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                  {order.shipping_address ? (
                    <>
                      <strong style={{ color: '#0f172a', display: 'block', marginBottom: '2px' }}>{order.shipping_address.full_name || order.shipping_address.name || 'Valued Customer'}</strong>
                      {order.shipping_address.street_address || order.shipping_address.street}<br />
                      {order.shipping_address.city}, {order.shipping_address.postal_code || order.shipping_address.postcode}<br />
                      {order.shipping_address.country}
                    </>
                  ) : (
                    <span>No delivery address recorded.</span>
                  )}
                </div>
              </div>

              <div style={{ gridColumn: '1 / -1' }}>
                <div style={{ fontSize: '12px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', marginBottom: '6px' }}>Billing Address</div>
                <div style={{ color: '#334155', lineHeight: 1.5, background: '#f8fafc', padding: '12px 16px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                  {order.billing_address || order.shipping_address ? (
                    (() => {
                      const b = order.billing_address || order.shipping_address;
                      return (
                        <>
                          <strong style={{ color: '#0f172a', display: 'block', marginBottom: '2px' }}>{b.full_name || b.name || order.user_name || 'Valued Customer'}</strong>
                          {b.street_address || b.street}<br />
                          {b.city}, {b.postal_code || b.postcode}<br />
                          {b.country}
                        </>
                      );
                    })()
                  ) : (
                    <span>No billing address recorded.</span>
                  )}
                </div>
              </div>

              <div>
                <div style={{ fontSize: '12px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', marginBottom: '6px' }}>Contact Phone</div>
                <div style={{ color: '#0f172a', fontWeight: 700 }}>
                  {order.shipping_address?.phone || order.guest_phone || 'N/A'}
                </div>
              </div>

              <div>
                <div style={{ fontSize: '12px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', marginBottom: '6px' }}>Payment Method</div>
                <div style={{ color: '#0f172a', fontWeight: 700 }}>
                  {order.payment_method || 'Credit / Debit Card'}
                </div>
              </div>

            </div>
          </div>

        </div>

        {/* RIGHT COLUMN: TIMELINE AUDIT TRAIL */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          
          <div className="table-card-box" style={{ padding: '24px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 900, color: '#0f172a', margin: '0 0 20px 0', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Clock size={18} color="#dc2626" />
              <span>Status Progression Timeline</span>
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', position: 'relative', paddingLeft: '10px' }}>
              
              {/* Step 1: Order Placed (Processing) */}
              <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#dc2626', marginTop: '3px', flexShrink: 0, boxShadow: '0 0 0 3px rgba(220, 38, 38, 0.15)' }} />
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 900, color: '#0f172a' }}>Order Placed (Processing)</div>
                  <div style={{ fontSize: '11.5px', color: '#64748b', marginTop: '2px' }}>
                    {new Date(order.created_at).toLocaleString()}
                  </div>
                </div>
              </div>

              {/* Status History Array Progression Entries */}
              {Array.isArray(order.status_history) && order.status_history.map((hist, hIdx) => {
                const isDelivered = hist.status === 'Delivered';
                const isCancelled = hist.status === 'Cancelled';
                const dotColor = isDelivered ? '#10b981' : isCancelled ? '#ef4444' : '#3b82f6';

                return (
                  <div key={hIdx} style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                    <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: dotColor, marginTop: '3px', flexShrink: 0, boxShadow: `0 0 0 3px ${dotColor}25` }} />
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 900, color: '#0f172a' }}>
                        {hist.status === 'Processing' ? 'Order Placed (Processing)' : `Advanced to ${hist.status}`}
                      </div>
                      {hist.comment && <div style={{ fontSize: '11.5px', color: '#475569', marginTop: '1px' }}>{hist.comment}</div>}
                      <div style={{ fontSize: '11.5px', color: '#64748b', marginTop: '2px' }}>
                        {new Date(hist.timestamp || hist.created_at || order.created_at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                );
              })}

            </div>
          </div>

        </div>

      </div>

      {/* MARK AS SHIPPED MODAL WITH OPTIONAL TRACKING NUMBER & COURIER */}
      {showShipModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '24px', padding: '32px', maxWidth: '440px', width: '100%', boxShadow: '0 20px 40px rgba(0,0,0,0.15)' }}>
            
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <div style={{ width: '52px', height: '52px', borderRadius: '50%', background: '#fef2f2', border: '1px solid #fee2e2', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                <Truck size={26} />
              </div>
              <h3 style={{ fontSize: '20px', fontWeight: 900, color: '#0f172a', margin: '0 0 4px 0' }}>Mark Order as Shipped</h3>
              <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>You can optionally add tracking and courier information for the customer notification email.</p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 800, color: '#0f172a', marginBottom: '6px' }}>Courier Partner (Optional)</label>
                <input
                  type="text"
                  placeholder="e.g. Royal Mail, DPD, DHL"
                  value={courierPartner}
                  onChange={(e) => setCourierPartner(e.target.value)}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '13px', outline: 'none', boxSizing: 'border-box' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 800, color: '#0f172a', marginBottom: '6px' }}>Tracking Number (Optional)</label>
                <input
                  type="text"
                  placeholder="e.g. GB123456789RM"
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '13px', outline: 'none', boxSizing: 'border-box' }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                type="button"
                onClick={() => setShowShipModal(false)}
                style={{ flex: 1, padding: '12px', borderRadius: '10px', border: '1px solid #cbd5e1', background: '#ffffff', color: '#475569', fontWeight: 800, fontSize: '13.5px', cursor: 'pointer' }}
              >
                Cancel
              </button>

              <button
                type="button"
                disabled={updating}
                onClick={() => handleAdvanceStatus('Shipped', { tracking_number: trackingNumber || undefined, courier: courierPartner || undefined })}
                style={{ flex: 1, padding: '12px', borderRadius: '10px', border: 'none', background: '#dc2626', color: '#ffffff', fontWeight: 900, fontSize: '13.5px', cursor: updating ? 'not-allowed' : 'pointer', boxShadow: '0 4px 12px rgba(220,38,38,0.25)' }}
              >
                {updating ? 'Updating...' : 'Confirm Shipped'}
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
};

export default OrderDetail;
